import React, { Suspense } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { legacyRedirectRoutes } from '@/lib/legacy-redirects';
import { MainLayout } from '@/components/layout/MainLayout';
import { PageLoading } from '@/components/ui/spinner';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { RoleProtectedRoute } from '@/components/auth/RoleProtectedRoute';

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('error loading dynamically imported module') ||
    error.message.includes('Importing a module script failed') ||
    // Chrome: a stale chunk path falls through the SPA rewrite to index.html
    // (text/html), and the module loader rejects the non-JS MIME type.
    error.message.includes('is not a valid JavaScript MIME type') ||
    error.name === 'ChunkLoadError'
  );
}

/**
 * Wraps React.lazy with a single retry via page reload.
 * Recovers users whose in-memory HTML references chunk filenames that
 * were replaced by a newer deploy. Only triggers on chunk-load errors —
 * other failures (bad code, real network errors) propagate to the
 * error boundary as normal.
 */
function lazyWithRetry<T extends React.ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return React.lazy(() => {
    const key = `chunk-retry:${window.location.pathname}`;
    return factory()
      .then((mod) => {
        sessionStorage.removeItem(key);
        return mod;
      })
      .catch((error: unknown) => {
        if (!isChunkLoadError(error)) throw error;
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, '1');
          window.location.reload();
          return new Promise<{ default: T }>(() => {});
        }
        sessionStorage.removeItem(key);
        throw error;
      });
  });
}

// Lazy load pages for better performance
const KnowledgeBasePage = lazyWithRetry(() =>
  import('@/pages/knowledge-base').then((m) => ({
    default: m.KnowledgeBasePage,
  })),
);
const DashboardPage = lazyWithRetry(() =>
  import('@/pages/dashboard').then((m) => ({ default: m.DashboardPage })),
);
const WarehousesPage = lazyWithRetry(() =>
  import('@/pages/warehouses').then((m) => ({ default: m.WarehousesPage })),
);
const WarehouseDetailPage = lazyWithRetry(() =>
  import('@/pages/warehouses/[id]').then((m) => ({
    default: m.WarehouseDetailPage,
  })),
);
const ItemsPage = lazyWithRetry(() =>
  import('@/pages/items').then((m) => ({ default: m.ItemsPage })),
);
const ItemDetailPage = lazyWithRetry(() =>
  import('@/pages/items/[id]').then((m) => ({ default: m.ItemDetailPage })),
);
const InventoryPage = lazyWithRetry(() =>
  import('@/pages/inventory').then((m) => ({ default: m.InventoryPage })),
);
const InventoryLedgerPage = lazyWithRetry(() =>
  import('@/pages/inventory/ledger').then((m) => ({
    default: m.InventoryLedgerPage,
  })),
);
const InventoryItemDetailPage = lazyWithRetry(() =>
  import('@/pages/inventory/[itemId]').then((m) => ({
    default: m.InventoryItemDetailPage,
  })),
);
const PurchaseOrdersPage = lazyWithRetry(() =>
  import('@/pages/purchase-orders/index').then((m) => ({
    default: m.PurchaseOrdersPage,
  })),
);
const PurchaseOrderDetailPage = lazyWithRetry(() =>
  import('@/pages/purchase-orders/[id]').then((m) => ({
    default: m.PurchaseOrderDetailPage,
  })),
);
const PutawayPage = lazyWithRetry(() =>
  import('@/pages/putaway').then((m) => ({ default: m.PutawayPage })),
);
const PickingPage = lazyWithRetry(() =>
  import('@/pages/picking').then((m) => ({ default: m.PickingPage })),
);
const PickOrderDetailPage = lazyWithRetry(() =>
  import('@/pages/picking/[id]').then((m) => ({
    default: m.PickOrderDetailPage,
  })),
);
const ShortPicksPage = lazyWithRetry(() =>
  import('@/pages/short-picks').then((m) => ({ default: m.ShortPicksPage })),
);
const GatherPage = lazyWithRetry(() =>
  import('@/pages/gather').then((m) => ({ default: m.GatherPage })),
);
const GatherListPage = lazyWithRetry(() =>
  import('@/pages/gather/[listId]').then((m) => ({
    default: m.GatherListPage,
  })),
);
const SettingsPage = lazyWithRetry(() =>
  import('@/pages/settings').then((m) => ({ default: m.SettingsPage })),
);
const ProfilePage = lazyWithRetry(() =>
  import('@/pages/profile').then((m) => ({ default: m.ProfilePage })),
);
const DeviceSettingsPage = lazyWithRetry(() =>
  import('@/pages/device-settings').then((m) => ({
    default: m.DeviceSettingsPage,
  })),
);
const SettingsLayoutPage = lazyWithRetry(() =>
  import('@/pages/settings/SettingsLayoutPage').then((m) => ({
    default: m.SettingsLayoutPage,
  })),
);
// C13: Roles & Permissions admin pages (temporary top-level placement;
// folds into C2's nested /settings/* block once C2 ships).
const RolesListPage = lazyWithRetry(() =>
  import('@/pages/settings/roles').then((m) => ({ default: m.RolesListPage })),
);
const RoleDetailPage = lazyWithRetry(() =>
  import('@/pages/settings/roles/[id]').then((m) => ({
    default: m.RoleDetailPage,
  })),
);
// C12: per-warehouse IP allowlist admin (USERNAME login enforcement)
const IpAllowlistPage = lazyWithRetry(() =>
  import('@/pages/settings/ip-allowlist').then((m) => ({
    default: m.IpAllowlistPage,
  })),
);
// Dashboard column visibility levels (admin-only column overrides)
const DashboardColumnsPage = lazyWithRetry(() =>
  import('@/pages/settings/dashboard-columns').then((m) => ({
    default: m.DashboardColumnsPage,
  })),
);
const ManufacturingSettingsPage = lazyWithRetry(() =>
  import('@/pages/settings/manufacturing').then((m) => ({
    default: m.ManufacturingSettingsPage,
  })),
);
const PrintersPage = lazyWithRetry(() =>
  import('@/pages/settings/printers').then((m) => ({
    default: m.PrintersPage,
  })),
);
const KitchenPlanningPage = lazyWithRetry(() =>
  import('@/pages/kitchens/planning').then((m) => ({
    default: m.KitchenPlanningPage,
  })),
);
const KitchenLabelsPage = lazyWithRetry(() =>
  import('@/pages/kitchen-labels').then((m) => ({
    default: m.KitchenLabelsPage,
  })),
);
const KitchenProjectDetailPage = lazyWithRetry(() =>
  import('@/pages/kitchen-labels/[id]').then((m) => ({
    default: m.KitchenProjectDetailPage,
  })),
);
const KitchenCatalogPage = lazyWithRetry(() =>
  import('@/pages/kitchens/catalog').then((m) => ({
    default: m.KitchenCatalogPage,
  })),
);
const KitchenCabinetDetailPage = lazyWithRetry(() =>
  import('@/pages/kitchens/catalog/cabinets/[id]').then((m) => ({
    default: m.KitchenCabinetDetailPage,
  })),
);
const KitchenProjectsPage = lazyWithRetry(() =>
  import('@/pages/kitchens/projects').then((m) => ({
    default: m.KitchenProjectsPage,
  })),
);
const KitchenProjectPage = lazyWithRetry(() =>
  import('@/pages/kitchens/projects/[id]').then((m) => ({
    default: m.KitchenProjectPage,
  })),
);
// Kitchens → Engineering (QA H-24): engineering review and engineering
// names on one page; the old names route redirects to it.
const KitchenEngineeringPage = lazyWithRetry(() =>
  import('@/pages/kitchens/engineering').then((m) => ({
    default: m.KitchenEngineeringPage,
  })),
);
const EngineeringNamesRedirect = lazyWithRetry(() =>
  import('@/pages/kitchens/engineering').then((m) => ({
    default: m.EngineeringNamesRedirect,
  })),
);
// Kitchens → Production (KM-5 / KM-6): the manufacturing page bodies with
// the line fixed to KITCHEN by each route element.
const KitchenProductionPage = lazyWithRetry(() =>
  import('@/pages/kitchens/production').then((m) => ({
    default: m.KitchenProductionPage,
  })),
);
const KitchenProductionWorkOrdersPage = lazyWithRetry(() =>
  import('@/pages/kitchens/production/work-orders').then((m) => ({
    default: m.KitchenProductionWorkOrdersPage,
  })),
);
const KitchenProductionWorkOrderPage = lazyWithRetry(() =>
  import('@/pages/kitchens/production/work-orders/[id]').then((m) => ({
    default: m.KitchenProductionWorkOrderPage,
  })),
);
const KitchenProductionBoardPage = lazyWithRetry(() =>
  import('@/pages/kitchens/production/board').then((m) => ({
    default: m.KitchenProductionBoardPage,
  })),
);
const KitchenProductionScanPage = lazyWithRetry(() =>
  import('@/pages/kitchens/production/scan').then((m) => ({
    default: m.KitchenProductionScanPage,
  })),
);
// Kitchens → Production → Count parts (2026-11-03): the kitchen end-of-line count.
const KitchenCountPage = lazyWithRetry(() =>
  import('@/pages/kitchens/production/count').then((m) => ({
    default: m.KitchenCountPage,
  })),
);
// Kitchens → Acumatica (PR-A2): items per project + failures, and the colour
// materials purchasing confirms.
const KitchenAcumaticaPage = lazyWithRetry(() =>
  import('@/pages/kitchens/acumatica').then((m) => ({
    default: m.KitchenAcumaticaPage,
  })),
);
const KitchenAcumaticaColoursPage = lazyWithRetry(() =>
  import('@/pages/kitchens/acumatica/colours').then((m) => ({
    default: m.KitchenAcumaticaColoursPage,
  })),
);
const KitchenAssemblyPage = lazyWithRetry(() =>
  import('@/pages/kitchens/assembly').then((m) => ({
    default: m.KitchenAssemblyPage,
  })),
);
const KitchenShippingPage = lazyWithRetry(() =>
  import('@/pages/kitchens/shipping').then((m) => ({
    default: m.KitchenShippingPage,
  })),
);
const LabelLookupPage = lazyWithRetry(() =>
  import('@/pages/label-lookup').then((m) => ({
    default: m.LabelLookupPage,
  })),
);
const ScanPage = lazyWithRetry(() =>
  import('@/pages/scan').then((m) => ({
    default: m.ScanPage,
  })),
);
const LabelTemplatesPage = lazyWithRetry(() =>
  import('@/pages/settings/label-templates').then((m) => ({
    default: m.LabelTemplatesPage,
  })),
);
const LabelTemplateDetailPage = lazyWithRetry(() =>
  import('@/pages/settings/label-templates/[id]').then((m) => ({
    default: m.LabelTemplateDetailPage,
  })),
);
const AlertPreferencesPage = lazyWithRetry(() =>
  import('@/pages/settings/alerts').then((m) => ({
    default: m.AlertPreferencesPage,
  })),
);
const ApiKeysPage = lazyWithRetry(() =>
  import('@/pages/admin/api-keys').then((m) => ({
    default: m.ApiKeysPage,
  })),
);
const UomPage = lazyWithRetry(() =>
  import('@/pages/uom').then((m) => ({ default: m.UomPage })),
);
const AdjustmentsPage = lazyWithRetry(() =>
  import('@/pages/adjustments').then((m) => ({ default: m.AdjustmentsPage })),
);
const AdjustmentDetailPage = lazyWithRetry(() =>
  import('@/pages/adjustments/[id]').then((m) => ({
    default: m.AdjustmentDetailPage,
  })),
);
const BulkReceivePage = lazyWithRetry(() =>
  import('@/pages/bulk-receive').then((m) => ({
    default: m.BulkReceivePage,
  })),
);
const BulkMovePage = lazyWithRetry(() =>
  import('@/pages/bulk-move').then((m) => ({
    default: m.BulkMovePage,
  })),
);
const BulkImportsPage = lazyWithRetry(() =>
  import('@/pages/bulk-imports').then((m) => ({
    default: m.BulkImportsPage,
  })),
);
const BulkImportDetailPage = lazyWithRetry(() =>
  import('@/pages/bulk-imports/[id]').then((m) => ({
    default: m.BulkImportDetailPage,
  })),
);
const TransferOrdersPage = lazyWithRetry(() =>
  import('@/pages/transfer-orders').then((m) => ({
    default: m.TransferOrdersPage,
  })),
);
const TransferOrderDetailPage = lazyWithRetry(() =>
  import('@/pages/transfer-orders/[id]').then((m) => ({
    default: m.TransferOrderDetailPage,
  })),
);
const KitAssemblyDetailPage = lazyWithRetry(() =>
  import('@/pages/kits/assembly/[id]').then((m) => ({
    default: m.KitAssemblyDetailPage,
  })),
);
const KitAssemblyPage = lazyWithRetry(() =>
  import('@/pages/kit-assembly').then((m) => ({ default: m.KitAssemblyPage })),
);
const ManufacturingDashboardPage = lazyWithRetry(() =>
  import('@/pages/manufacturing-dashboard').then((m) => ({
    default: m.ManufacturingDashboardPage,
  })),
);
const ManufacturingWorkOrdersPage = lazyWithRetry(() =>
  import('@/pages/manufacturing-work-orders').then((m) => ({
    default: m.ManufacturingWorkOrdersPage,
  })),
);
const ManufacturingWorkOrderDetailPage = lazyWithRetry(() =>
  import('@/pages/manufacturing-work-orders/[id]').then((m) => ({
    default: m.ManufacturingWorkOrderDetailPage,
  })),
);
const ManufacturingScanPage = lazyWithRetry(() =>
  import('@/pages/manufacturing-scan').then((m) => ({
    default: m.ManufacturingScanPage,
  })),
);
const ManufacturingBoardPage = lazyWithRetry(() =>
  import('@/pages/manufacturing-board').then((m) => ({
    default: m.ManufacturingBoardPage,
  })),
);
// End-of-line count (2026-10-30): the Count parts tablet page.
const ManufacturingCountPage = lazyWithRetry(() =>
  import('@/pages/manufacturing-count').then((m) => ({
    default: m.ManufacturingCountPage,
  })),
);
const KitPutawayPage = lazyWithRetry(() =>
  import('@/pages/kit-putaway/index').then((m) => ({
    default: m.KitPutawayPage,
  })),
);
const SkuNeedsLayout = lazyWithRetry(() =>
  import('@/pages/sku-needs/layout').then((m) => ({
    default: m.SkuNeedsLayout,
  })),
);
const SkuNeedsPage = lazyWithRetry(() =>
  import('@/pages/sku-needs').then((m) => ({
    default: m.SkuNeedsPage,
  })),
);
const ShipSchedulePage = lazyWithRetry(() =>
  import('@/pages/sku-needs/ship-schedule').then((m) => ({
    default: m.ShipSchedulePage,
  })),
);
const PutawayRulesPage = lazyWithRetry(() =>
  import('@/pages/putaway-rules').then((m) => ({
    default: m.PutawayRulesPage,
  })),
);
const AllocationPriorityRulesPage = lazyWithRetry(() =>
  import('@/pages/allocation-priority-rules').then((m) => ({
    default: m.AllocationPriorityRulesPage,
  })),
);
const AllocationSettingsPage = lazyWithRetry(() =>
  import('@/pages/allocation-settings').then((m) => ({
    default: m.AllocationSettingsPage,
  })),
);
const PickSplitRulesPage = lazyWithRetry(() =>
  import('@/pages/pick-split-rules').then((m) => ({
    default: m.PickSplitRulesPage,
  })),
);
const ChannelRulesPage = lazyWithRetry(() =>
  import('@/pages/channel-rules').then((m) => ({
    default: m.ChannelRulesPage,
  })),
);
const SoRoutingRulesPage = lazyWithRetry(() =>
  import('@/pages/so-routing-rules').then((m) => ({
    default: m.SoRoutingRulesPage,
  })),
);
const BinReservationRulesPage = lazyWithRetry(() =>
  import('@/pages/bin-reservation-rules').then((m) => ({
    default: m.BinReservationRulesPage,
  })),
);
const BinPriorityRulesPage = lazyWithRetry(() =>
  import('@/pages/bin-priority-rules').then((m) => ({
    default: m.BinPriorityRulesPage,
  })),
);
const PickPathRulesPage = lazyWithRetry(() =>
  import('@/pages/pick-path-rules').then((m) => ({
    default: m.PickPathRulesPage,
  })),
);
const AllocationStrategyRulesPage = lazyWithRetry(() =>
  import('@/pages/allocation-strategy-rules').then((m) => ({
    default: m.AllocationStrategyRulesPage,
  })),
);
const AutoTagRulesPage = lazyWithRetry(() =>
  import('@/pages/auto-tag-rules').then((m) => ({
    default: m.AutoTagRulesPage,
  })),
);
const SkuReplacementRulesPage = lazyWithRetry(() =>
  import('@/pages/sku-replacement-rules').then((m) => ({
    default: m.SkuReplacementRulesPage,
  })),
);
const ItemTypeOverridesPage = lazyWithRetry(() =>
  import('@/pages/item-type-overrides').then((m) => ({
    default: m.ItemTypeOverridesPage,
  })),
);
const LayoutChangeRequestsPage = lazyWithRetry(() =>
  import('@/pages/settings/layout-change-requests').then((m) => ({
    default: m.LayoutChangeRequestsPage,
  })),
);
const AssignmentRulesPage = lazyWithRetry(() =>
  import('@/pages/assignment-rules').then((m) => ({
    default: m.AssignmentRulesPage,
  })),
);
const WorkforcePage = lazyWithRetry(() =>
  import('@/pages/workforce').then((m) => ({
    default: m.WorkforcePage,
  })),
);
const TagsPage = lazyWithRetry(() =>
  import('@/pages/tags').then((m) => ({
    default: m.TagsPage,
  })),
);
const OrderHoldsPage = lazyWithRetry(() =>
  import('@/pages/order-holds').then((m) => ({
    default: m.OrderHoldsPage,
  })),
);
const WarehouseRulesPage = lazyWithRetry(() =>
  import('@/pages/warehouse-rules').then((m) => ({
    default: m.WarehouseRulesPage,
  })),
);
const UnifiedLocationsPage = lazyWithRetry(() =>
  import('@/pages/locations').then((m) => ({
    default: m.UnifiedLocationsPage,
  })),
);
const LocationDetailPage = lazyWithRetry(() =>
  import('@/pages/locations/[id]').then((m) => ({
    default: m.LocationDetailPage,
  })),
);
const ReportsPage = lazyWithRetry(() =>
  import('@/pages/reports').then((m) => ({ default: m.ReportsPage })),
);
const ShipHawkSyncReconcileReportPage = lazyWithRetry(() =>
  import('@/pages/reports/shiphawk-sync').then((m) => ({
    default: m.ShipHawkSyncReconcileReportPage,
  })),
);
const NotificationsPage = lazyWithRetry(() =>
  import('@/pages/notifications').then((m) => ({
    default: m.NotificationsPage,
  })),
);
const UsersPage = lazyWithRetry(() =>
  import('@/pages/users').then((m) => ({ default: m.UsersPage })),
);
const OrdersDashboardPage = lazyWithRetry(() =>
  import('@/pages/orders-dashboard').then((m) => ({
    default: m.OrdersDashboardPage,
  })),
);
const SalesOrdersPage = lazyWithRetry(() =>
  import('@/pages/sales-orders').then((m) => ({ default: m.SalesOrdersPage })),
);
const SalesOrderDetailPage = lazyWithRetry(() =>
  import('@/pages/sales-orders/[id]').then((m) => ({
    default: m.SalesOrderDetailPage,
  })),
);
const VendorsPage = lazyWithRetry(() =>
  import('@/pages/vendors').then((m) => ({ default: m.VendorsPage })),
);
const VendorDetailPage = lazyWithRetry(() =>
  import('@/pages/vendors/[id]').then((m) => ({ default: m.VendorDetailPage })),
);
const ChatPage = lazyWithRetry(() =>
  import('@/pages/chat').then((m) => ({ default: m.ChatPage })),
);
const AssistantPage = lazyWithRetry(() =>
  import('@/pages/assistant').then((m) => ({ default: m.AssistantPage })),
);
const RecountPage = lazyWithRetry(() =>
  import('@/pages/recount').then((m) => ({ default: m.RecountPage })),
);
const RecountDetailPage = lazyWithRetry(() =>
  import('@/pages/recount/[id]').then((m) => ({
    default: m.RecountDetailPage,
  })),
);
const CountBatchesPage = lazyWithRetry(() =>
  import('@/pages/count-batches').then((m) => ({
    default: m.CountBatchesPage,
  })),
);
const CountBatchDetailPage = lazyWithRetry(() =>
  import('@/pages/count-batches/[id]').then((m) => ({
    default: m.CountBatchDetailPage,
  })),
);
const CountingPage = lazyWithRetry(() =>
  import('@/pages/counting').then((m) => ({ default: m.CountingPage })),
);
const CountingAislePage = lazyWithRetry(() =>
  import('@/pages/counting/aisle').then((m) => ({
    default: m.CountingAislePage,
  })),
);
const CountTaskPage = lazyWithRetry(() =>
  import('@/pages/counting/[taskId]').then((m) => ({
    default: m.CountTaskPage,
  })),
);
const WrappingPage = lazyWithRetry(() =>
  import('@/pages/wrapping').then((m) => ({ default: m.WrappingPage })),
);
const WrappingDetailPage = lazyWithRetry(() =>
  import('@/pages/wrapping/[id]').then((m) => ({
    default: m.WrappingDetailPage,
  })),
);
const SyncDashboardPage = lazyWithRetry(() =>
  import('@/pages/sync-dashboard').then((m) => ({
    default: m.SyncDashboardPage,
  })),
);
const SyncConflictsPage = lazyWithRetry(() =>
  import('@/pages/sync-conflicts').then((m) => ({
    default: m.SyncConflictsPage,
  })),
);
const ActionsPage = lazyWithRetry(() =>
  import('@/pages/actions').then((m) => ({ default: m.ActionsPage })),
);
const MyStatsPage = lazyWithRetry(() =>
  import('@/pages/my-stats').then((m) => ({ default: m.MyStatsPage })),
);
const WrapPhotosPage = lazyWithRetry(() =>
  import('@/pages/wrap-photos').then((m) => ({ default: m.WrapPhotosPage })),
);
// TEMPORARY FEATURE — external-fulfillment reconcile (admin-only). Remove this
// lazy import + its <Route> below once external fulfillment stops.
const ExternalFulfillmentReconcilePage = lazyWithRetry(() =>
  import('@/pages/external-fulfillment-reconcile').then((m) => ({
    default: m.ExternalFulfillmentReconcilePage,
  })),
);
const LoginPage = lazyWithRetry(() =>
  import('@/pages/auth/login').then((m) => ({ default: m.LoginPage })),
);
const AuthActionPage = lazyWithRetry(() =>
  import('@/pages/auth/action').then((m) => ({ default: m.AuthActionPage })),
);

function NotFoundPage() {
  return (
    <div className="flex h-[calc(100vh-8rem)] items-center justify-center">
      <div className="text-center">
        <h2 className="text-2xl font-semibold text-muted-foreground">
          Page Not Found
        </h2>
        <p className="mt-2 text-muted-foreground">
          The page you're looking for doesn't exist.
        </p>
      </div>
    </div>
  );
}

function ProtectedMainLayout() {
  return (
    <ProtectedRoute>
      <MainLayout />
    </ProtectedRoute>
  );
}

function RoleProtectedLayout() {
  return (
    <RoleProtectedRoute>
      <Outlet />
    </RoleProtectedRoute>
  );
}

function App() {
  return (
    <Suspense fallback={<PageLoading />}>
      <Routes>
        {/* Public routes (no auth required) */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/action" element={<AuthActionPage />} />

        <Route element={<ProtectedMainLayout />}>
          <Route element={<RoleProtectedLayout />}>
            {/* Dashboard */}
            <Route path="/" element={<DashboardPage />} />

            {/* Reference Data */}
            <Route path="/warehouses" element={<WarehousesPage />} />
            <Route path="/warehouses/:id" element={<WarehouseDetailPage />} />
            <Route path="/items" element={<ItemsPage />} />
            <Route path="/items/:id" element={<ItemDetailPage />} />
            <Route path="/uom" element={<UomPage />} />
            <Route path="/vendors" element={<VendorsPage />} />
            <Route path="/vendors/:id" element={<VendorDetailPage />} />

            {/* Inventory */}
            <Route path="/inventory" element={<InventoryPage />} />
            <Route path="/inventory/ledger" element={<InventoryLedgerPage />} />
            <Route path="/adjustments" element={<AdjustmentsPage />} />
            <Route path="/adjustments/:id" element={<AdjustmentDetailPage />} />
            <Route
              path="/inventory/bulk-receive"
              element={<BulkReceivePage />}
            />
            <Route path="/inventory/bulk-move" element={<BulkMovePage />} />
            <Route
              path="/inventory/bulk-imports"
              element={<BulkImportsPage />}
            />
            <Route
              path="/inventory/bulk-imports/:id"
              element={<BulkImportDetailPage />}
            />
            {/* Dynamic per-SKU audit detail. React Router v6 ranks the static
                /inventory/{ledger,bulk-receive,bulk-imports} routes above this
                dynamic segment regardless of declaration order. */}
            <Route
              path="/inventory/:itemId"
              element={<InventoryItemDetailPage />}
            />
            <Route path="/transfer-orders" element={<TransferOrdersPage />} />
            <Route
              path="/transfer-orders/:id"
              element={<TransferOrderDetailPage />}
            />

            {/* Inbound */}
            <Route path="/purchase-orders" element={<PurchaseOrdersPage />} />
            <Route
              path="/purchase-orders/:id"
              element={<PurchaseOrderDetailPage />}
            />
            <Route path="/putaway" element={<PutawayPage />} />

            {/* Outbound */}
            <Route path="/picking" element={<PickingPage />} />
            <Route path="/picking/:id" element={<PickOrderDetailPage />} />
            <Route path="/short-picks" element={<ShortPicksPage />} />
            {/* Phase-2 gathering — the floor screen that moves stock from pick
                bins into the staging area before a picker is assigned. */}
            <Route path="/gather" element={<GatherPage />} />
            <Route path="/gather/:listId" element={<GatherListPage />} />

            {/* Recount */}
            <Route path="/recount" element={<RecountPage />} />
            <Route path="/recount/:id" element={<RecountDetailPage />} />

            {/* Counting (cycle counting v2 — counter-facing) */}
            <Route path="/counting" element={<CountingPage />} />
            {/* Declared BEFORE /counting/:taskId — v6 ranks static segments
                above dynamic ones, so this is belt-and-braces, but the aisle
                drilldown must never resolve as a task whose id is "aisle". */}
            <Route path="/counting/aisle" element={<CountingAislePage />} />
            <Route path="/counting/:taskId" element={<CountTaskPage />} />

            {/* Count batches (cycle counting v2 — manager/admin) */}
            <Route path="/count-batches" element={<CountBatchesPage />} />
            <Route
              path="/count-batches/:id"
              element={<CountBatchDetailPage />}
            />

            {/* Wrapping */}
            <Route path="/wrapping" element={<WrappingPage />} />
            <Route path="/wrapping/:id" element={<WrappingDetailPage />} />

            {/* Manufacturing */}
            <Route
              path="/kits/assembly/:id"
              element={<KitAssemblyDetailPage />}
            />
            <Route path="/kit-assembly" element={<KitAssemblyPage />} />
            <Route path="/kit-putaway" element={<KitPutawayPage />} />
            <Route
              path="/manufacturing-dashboard"
              element={<ManufacturingDashboardPage />}
            />
            {/* SKU Needs is a tabbed section: the layout owns the tab bar, the
                children are the tabs. Child paths are ABSOLUTE so each one is
                enumerable in ROUTE_PERMISSIONS (route-coverage.test.ts parses
                these `path=` literals). */}
            <Route path="/sku-needs" element={<SkuNeedsLayout />}>
              <Route index element={<SkuNeedsPage />} />
              <Route
                path="/sku-needs/ship-schedule"
                element={<ShipSchedulePage />}
              />
            </Route>
            <Route
              path="/manufacturing-work-orders"
              element={<ManufacturingWorkOrdersPage />}
            />
            {/* Flat, not under the /settings layout: /settings excludes
                manufacturing managers, the people this page is for. */}
            <Route
              path="/settings/manufacturing"
              element={<ManufacturingSettingsPage />}
            />

            {/* mfg-program PR-F */}
            <Route
              path="/manufacturing-work-orders/:id"
              element={<ManufacturingWorkOrderDetailPage />}
            />
            {/* mfg-program PR-H: stack-label scan station */}
            <Route
              path="/manufacturing-scan"
              element={<ManufacturingScanPage />}
            />
            {/* End-of-line count (2026-10-30): Count parts on the tablet. */}
            <Route
              path="/manufacturing-count"
              element={<ManufacturingCountPage />}
            />
            {/* Big-screen board for the plant (2026-10-20): read-only. */}
            <Route
              path="/manufacturing-board"
              element={<ManufacturingBoardPage />}
            />

            {/* Operations — /locations is the one unified surface for all 10
                LocationType values; /slotting and /operational-locations now
                redirect into its family tabs (see lib/legacy-redirects.tsx). */}
            <Route path="/locations" element={<UnifiedLocationsPage />} />
            <Route path="/locations/:id" element={<LocationDetailPage />} />

            {/* Legacy paths kept alive so old links and bookmarks resolve.
                Declared as data in lib/legacy-redirects.tsx — add an entry
                there, not a scattered <Route> here. They carry no permission
                entry at all; the target they forward to enforces access. The
                routes come from that file too, so the render test mounts the
                same JSX this does. */}
            {legacyRedirectRoutes()}

            {/* Settings (parent + sub-pages) */}
            <Route path="/settings" element={<SettingsLayoutPage />}>
              <Route
                index
                element={<Navigate to="/settings/configuration" replace />}
              />
              <Route path="configuration" element={<SettingsPage />} />
              <Route path="warehouse" element={<WarehouseRulesPage />} />
              <Route
                path="priority-rules"
                element={<AllocationPriorityRulesPage />}
              />
              <Route path="allocation" element={<AllocationSettingsPage />} />
              <Route path="putaway" element={<PutawayRulesPage />} />
              <Route path="pick-split" element={<PickSplitRulesPage />} />
              <Route path="channel" element={<ChannelRulesPage />} />
              <Route
                path="bin-reservation"
                element={<BinReservationRulesPage />}
              />
              <Route path="bin-priority" element={<BinPriorityRulesPage />} />
              <Route path="pick-path" element={<PickPathRulesPage />} />
              <Route
                path="allocation-strategy"
                element={<AllocationStrategyRulesPage />}
              />
              <Route path="so-routing" element={<SoRoutingRulesPage />} />
              <Route path="auto-tag" element={<AutoTagRulesPage />} />
              <Route
                path="sku-replacement-rules"
                element={<SkuReplacementRulesPage />}
              />
              <Route
                path="item-type-overrides"
                element={<ItemTypeOverridesPage />}
              />
              <Route
                path="layout-change-requests"
                element={<LayoutChangeRequestsPage />}
              />
              <Route path="assignment" element={<AssignmentRulesPage />} />
              <Route path="tags" element={<TagsPage />} />
              <Route path="order-holds" element={<OrderHoldsPage />} />
            </Route>

            {/* Sales */}
            <Route path="/orders-dashboard" element={<OrdersDashboardPage />} />
            <Route path="/sales-orders" element={<SalesOrdersPage />} />
            <Route
              path="/sales-orders/:id"
              element={<SalesOrderDetailPage />}
            />

            {/* Administration */}
            <Route path="/users" element={<UsersPage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route
              path="/reports/shiphawk-sync"
              element={<ShipHawkSyncReconcileReportPage />}
            />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/chat" element={<ChatPage />} />
            <Route path="/assistant" element={<AssistantPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/device-settings" element={<DeviceSettingsPage />} />
            {/* Personal alert opt-in/out — reachable by every authenticated user */}
            <Route path="/settings/alerts" element={<AlertPreferencesPage />} />
            {/* C13: roles admin — temporary top-level; folds into C2 nested /settings block */}
            <Route path="/settings/roles" element={<RolesListPage />} />
            <Route path="/settings/roles/:id" element={<RoleDetailPage />} />
            <Route
              path="/settings/ip-allowlist"
              element={<IpAllowlistPage />}
            />
            <Route
              path="/settings/dashboard-columns"
              element={<DashboardColumnsPage />}
            />
            {/*
              Top-level, not under /settings: scanning a serial is a floor
              activity, and burying it in settings would put it behind chrome
              an operator with a scanner never opens.
            */}
            <Route path="/label-lookup" element={<LabelLookupPage />} />
            {/* Kitchen module (K-8): purchasing + production planning. */}
            <Route
              path="/kitchens/planning"
              element={<KitchenPlanningPage />}
            />
            {/* Legacy Labels (K-18, interim): the old kitchen label upload + 6x4 printing; new projects go to /kitchens/projects. */}
            <Route path="/kitchen-labels" element={<KitchenLabelsPage />} />
            <Route
              path="/kitchen-labels/:id"
              element={<KitchenProjectDetailPage />}
            />
            {/* Kitchen module (K-3): the kitchen product catalog. */}
            <Route path="/kitchens/catalog" element={<KitchenCatalogPage />} />
            <Route
              path="/kitchens/catalog/cabinets/:id"
              element={<KitchenCabinetDetailPage />}
            />
            {/* Kitchen module (K-6): multi-family kitchen projects. */}
            <Route
              path="/kitchens/projects"
              element={<KitchenProjectsPage />}
            />
            <Route
              path="/kitchens/projects/:id"
              element={<KitchenProjectPage />}
            />
            {/* Kitchens → Engineering (K-9 review + PR-E2 names, one page since QA H-24). */}
            <Route
              path="/kitchens/engineering"
              element={<KitchenEngineeringPage />}
            />
            <Route
              path="/kitchens/engineering/:id"
              element={<KitchenEngineeringPage />}
            />
            {/* The old Engineering Names page: redirects, keeping its tab. */}
            <Route
              path="/kitchens/engineering-names"
              element={<EngineeringNamesRedirect />}
            />
            {/* Kitchens → Acumatica (PR-A2). */}
            <Route
              path="/kitchens/acumatica"
              element={<KitchenAcumaticaPage />}
            />
            <Route
              path="/kitchens/acumatica/colours"
              element={<KitchenAcumaticaColoursPage />}
            />
            {/* Kitchens → Production (KM-5 / KM-6): kitchen orders on the
                manufacturing engine, only ever under /kitchens/production*. */}
            <Route
              path="/kitchens/production"
              element={<KitchenProductionPage />}
            />
            <Route
              path="/kitchens/production/work-orders"
              element={<KitchenProductionWorkOrdersPage />}
            />
            <Route
              path="/kitchens/production/work-orders/:id"
              element={<KitchenProductionWorkOrderPage />}
            />
            <Route
              path="/kitchens/production/board"
              element={<KitchenProductionBoardPage />}
            />
            <Route
              path="/kitchens/production/scan"
              element={<KitchenProductionScanPage />}
            />
            <Route
              path="/kitchens/production/count"
              element={<KitchenCountPage />}
            />
            {/* Kitchens → Assembly (PR-L1): the assembly station. */}
            <Route
              path="/kitchens/assembly"
              element={<KitchenAssemblyPage />}
            />
            {/* Kitchens → Shipping (PR-S1): the loading dock. */}
            <Route
              path="/kitchens/shipping"
              element={<KitchenShippingPage />}
            />
            <Route
              path="/kitchens/shipping/loads/:loadId"
              element={<KitchenShippingPage />}
            />
            {/* Same reasoning, more so: /scan IS the floor tool. */}
            <Route path="/scan" element={<ScanPage />} />
            <Route path="/settings/printers" element={<PrintersPage />} />
            {/*
              Flat top-level paths, not nested under SettingsLayoutPage: the
              designer needs a full-bleed canvas and the settings sidebar chrome
              would eat the horizontal space.
            */}
            <Route
              path="/settings/label-templates"
              element={<LabelTemplatesPage />}
            />
            <Route
              path="/settings/label-templates/:id"
              element={<LabelTemplateDetailPage />}
            />
            <Route path="/settings/api-keys" element={<ApiKeysPage />} />
            <Route path="/sync-dashboard" element={<SyncDashboardPage />} />
            <Route path="/sync-conflicts" element={<SyncConflictsPage />} />
            <Route path="/actions" element={<ActionsPage />} />
            <Route path="/my-stats" element={<MyStatsPage />} />
            <Route path="/workforce" element={<WorkforcePage />} />
            <Route path="/wrap-photos" element={<WrapPhotosPage />} />
            {/* TEMPORARY FEATURE — external-fulfillment reconcile (admin-only).
                Remove this route once external fulfillment stops. */}
            <Route
              path="/external-fulfillment-reconcile"
              element={<ExternalFulfillmentReconcilePage />}
            />
          </Route>

          {/* Guides require sign-in, but no operational role or warehouse assignment. */}
          <Route path="/knowledge-base" element={<KnowledgeBasePage />} />
          <Route
            path="/knowledge-base/:articleId"
            element={<KnowledgeBasePage />}
          />

          {/* 404 -- outside RoleProtectedLayout so it renders without role checks */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

export default App;
